<template>
  <div class="book-new-data-wrapper" v-loading.fullscreen.lock="loading">
    <!-- 筛选区域：唯一筛选是日期(run_date) -->
    <div class="filter-section">
      <el-card shadow="hover">
        <div class="filter-content">
          <div class="filter-item">
            <label class="filter-label">数据日期：</label>
            <el-date-picker
              v-model="runDate"
              type="date"
              placeholder="选择数据日期"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              :picker-options="pickerOptions"
              style="width: 180px;"
              @change="handleDateChange"
            />
          </div>
          <span v-if="availableDates.length === 0" class="filter-tip-inline">暂无预计算数据，请等待凌晨任务或运行补数据脚本</span>
        </div>
        <div class="filter-tip">
          数据每天凌晨预计算。日期范围 = 用户注册日期；N日内 = 相对各自注册日的行为窗口（截至跑数当天）；渠道为全部用户。<br />
          <strong>每本书均已剔除「以该书为初始小说」的用户（1.0.16 起首启直连进入的书），整条漏斗衡量书城自然转化；该用户对其他书仍照常计入。圈选同平台页：仅统计 App 版本 ≥ 1.0.13 的用户，1.0.16 及之后无手机号的用户不算注册成功。2026-07-07 及之后的数据日期为此新口径，更早为旧口径。</strong><br />
          进入剧情 / 各章完成均按 N日内窗口的 dialogues 判定；进入率 = 进入/点击，第 N 章完成率 = 完成第 N 章人数 / 进入人数，付费率 = 在该书内消费过能量(果肉)人数 / 进入人数，消费含解锁剧情、服装、商城和 AI 对话按条扣费（2026-07-16 上线）。<br />
          发 N 条消息率 = 在该书内发出的消息条数达 N 条的人数 / 进入人数。只算用户在 AI 剧情里<strong>亲自发的</strong>消息（不含固定剧情台词），同一条消息在多份存档副本里只算一次——与「小说数据」「广告数据」两页同口径，可直接对比。<br />
          注：埋点(列表曝光/点击)自 2026-05-21 上线，注册早于此的看板曝光/点击会偏低，不影响进入/章节。<br />
          注：发消息率不在每日预计算里，打开页面时实时补算，所以<strong>任意历史数据日期都有数</strong>，但首次打开需等几秒。统计范围与同一行其他列一致：行为都截至该数据日跑数的时刻（次日凌晨），圈人也按当天的口径。
        </div>
      </el-card>
    </div>

    <!-- 8 个看板区块（本页仅管理员可进，不再按角色过滤看板） -->
    <div v-for="board in boards" :key="board.board_key" class="result-section">
      <div class="board-head">
        <h3 class="section-title">{{ board.board_name }}</h3>
        <el-button
          size="mini"
          icon="el-icon-download"
          :loading="exportingBoard === board.board_key"
          :disabled="!board.rows.length"
          @click="handleExportBoard(board)"
        >导出 Excel</el-button>
      </div>
      <div class="board-sub">注册范围：{{ board.reg_start }} ~ {{ prevDay(board.reg_end) }}（{{ board.n_days }}日内）</div>
      <el-table :data="board.rows" style="width: 100%" border max-height="560">
        <el-table-column prop="book_title" label="书名" width="180" fixed show-overflow-tooltip>
          <template slot-scope="scope">
            <router-link
              :to="{ path: '/churn-analysis', query: { game_id: scope.row.book_id, start_date: board.reg_start, end_date: prevDay(board.reg_end), n_days: board.n_days } }"
              class="book-link"
            >{{ scope.row.book_title }}</router-link>
          </template>
        </el-table-column>
        <el-table-column prop="book_id" label="书ID" width="80" fixed align="right" />
        <el-table-column prop="impression_uv" label="列表页曝光人数" width="120" align="right" />
        <el-table-column prop="detail_view_uv" label="列表页点击人数" width="120" align="right" />
        <el-table-column label="点击率" width="90" align="right">
          <template slot-scope="scope">{{ formatPercent(clickRate(scope.row)) }}</template>
        </el-table-column>
        <el-table-column prop="enter_uv" label="进入剧情人数" width="110" align="right" />
        <el-table-column label="进入率" width="90" align="right">
          <template slot-scope="scope">{{ formatPercent(enterRate(scope.row)) }}</template>
        </el-table-column>
        <el-table-column
          v-for="chapter in 15"
          :key="'ch' + chapter"
          :label="'第' + chapter + '章完成率'"
          width="110"
          align="right"
        >
          <template slot-scope="scope">{{ formatPercent(chapterRate(scope.row, chapter)) }}</template>
        </el-table-column>
        <el-table-column
          v-for="mc in msgCols"
          :key="mc.field"
          :label="mc.label"
          width="120"
          align="right"
        >
          <template slot-scope="scope">
            <span v-if="msgLoading" class="msg-pending">计算中…</span>
            <span v-else-if="msgFailed" class="msg-pending">-</span>
            <span v-else>{{ formatPercent(msgRate(scope.row, mc.field)) }}</span>
          </template>
        </el-table-column>
        <el-table-column
          v-for="pc in payCols"
          :key="pc.field"
          :label="pc.label"
          width="110"
          align="right"
        >
          <template slot-scope="scope">{{ formatPercent(payRate(scope.row, pc.field)) }}</template>
        </el-table-column>
        <template slot="empty">暂无数据</template>
      </el-table>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'
import { getBookDaily, getBookDailyDates, getBookDailyMsg, exportBookDaily, BookNewDataRow, BookDailyBoard } from '@/api/bookNewData'

@Component
export default class NewDataAnalysis extends Vue {
  // 仅用于吸收 router-view 透传的 prop，避免 attribute fallthrough，本页不使用
  @Prop() readonly game?: unknown
  @Prop() readonly games?: unknown

  loading = false
  runDate = ''
  // 正在导出的看板 key（每个表各自一个按钮，只让被点的那个转圈）
  exportingBoard = ''

  // 发消息几列是实时补算的（不在预计算表里），主表先渲染、这几列后到。
  // 到位前显示「计算中…」而不是 0.0%，免得被当成真的没人发消息。
  msgLoading = false
  msgFailed = false
  private msgSeq = 0
  availableDates: string[] = []
  boards: BookDailyBoard[] = []

  // 发消息分层列：阈值即消息条数；分母均为进入剧情人数（enter_uv）。
  msgCols = [
    { field: 'msg1', label: '发1条消息率' },
    { field: 'msg3', label: '发3条消息率' },
    { field: 'msg5', label: '发5条消息率' },
    { field: 'msg10', label: '发10条消息率' },
    { field: 'msg20', label: '发20条消息率' },
    { field: 'msg30', label: '发30条消息率' },
    { field: 'msg50', label: '发50条消息率' },
    { field: 'msg100', label: '发100条消息率' }
  ]

  // 付费（能量消费）分层列：能量=果肉，阈值即果肉数；分母均为进入剧情人数（enter_uv）。
  payCols = [
    { field: 'pay0', label: '付费率' },
    { field: 'pay10', label: '付费10能量率' },
    { field: 'pay20', label: '付费20能量率' },
    { field: 'pay30', label: '付费30能量率' },
    { field: 'pay50', label: '付费50能量率' },
    { field: 'pay100', label: '付费100能量率' },
    { field: 'pay300', label: '付费300能量率' }
  ]

  // 只允许选有数据的日期
  get pickerOptions() {
    const set = new Set(this.availableDates)
    return {
      disabledDate: (d: Date) => !set.has(this.fmt(d))
    }
  }

  async created() {
    this.loading = true
    try {
      this.availableDates = await getBookDailyDates()
      if (this.availableDates.length > 0) {
        this.runDate = this.availableDates[0] // 最新一天
        await this.loadBoards()
      }
    } catch (error) {
      console.error('获取分书每日预计算数据失败:', error)
    } finally {
      this.loading = false
    }
  }

  async handleDateChange() {
    if (!this.runDate) return
    this.loading = true
    try {
      await this.loadBoards()
    } finally {
      this.loading = false
    }
  }

  async loadBoards() {
    const resp = await getBookDaily(this.runDate)
    this.boards = resp.boards || []
    // 主表已经可以渲染了，发消息几列不等它、异步补
    this.loadMsgTiers()
  }

  // 拉发消息分层并填进已渲染的行。这几列不在预计算表里，是后端实时算的，
  // 首次约 6~7 秒，之后命中后端缓存秒回。失败只影响这几列，主表照常可用。
  async loadMsgTiers() {
    const runDate = this.runDate
    const seq = ++this.msgSeq
    this.msgLoading = true
    this.msgFailed = false
    try {
      const resp = await getBookDailyMsg(runDate)
      // 期间用户切了日期 / 又点了一次，丢弃这次的旧结果
      if (seq !== this.msgSeq || runDate !== this.runDate) return

      const fields = resp.thresholds.map(t => 'msg' + t)
      for (const board of this.boards) {
        const byBook = new Map((resp.boards[board.board_key] || []).map(m => [m.book_id, m.tiers]))
        for (const row of board.rows) {
          const tiers = byBook.get(row.book_id)
          const target = row as unknown as Record<string, number>
          fields.forEach((f, i) => {
            // 该书这次没返回（窗口内无人进入）就补 0；字段在 /book-daily 的响应里已存在，
            // 直接赋值即可保持响应式，无需 Vue.set
            target[f] = tiers ? tiers[i] : 0
          })
        }
      }
    } catch (error) {
      if (seq !== this.msgSeq) return
      console.error('获取发消息数据失败:', error)
      this.msgFailed = true
    } finally {
      if (seq === this.msgSeq) {
        this.msgLoading = false
      }
    }
  }

  fmt(d: Date): string {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  // reg_end 是「不含」的次日 0 点(= run_date)，展示/下钻用其前一天
  prevDay(dateStr: string): string {
    if (!dateStr) return ''
    const d = new Date(dateStr + 'T00:00:00')
    d.setDate(d.getDate() - 1)
    return this.fmt(d)
  }

  formatPercent(value: number): string {
    if (value === undefined || value === null || isNaN(value)) return '-'
    return (value * 100).toFixed(1) + '%'
  }

  rate(numerator: number, denominator: number): number {
    if (!denominator) return NaN
    return numerator / denominator
  }

  clickRate(row: BookNewDataRow): number {
    return this.rate(row.detail_view_uv, row.impression_uv)
  }

  enterRate(row: BookNewDataRow): number {
    return this.rate(row.enter_uv, row.detail_view_uv)
  }

  chapterRate(row: BookNewDataRow, chapter: number): number {
    const reached = (row as unknown as Record<string, number>)['ch' + chapter] || 0
    return this.rate(reached, row.enter_uv)
  }

  // 发N条消息率 = 在该书内发出的消息条数达阈值的人数 / 进入剧情人数
  msgRate(row: BookNewDataRow, field: string): number {
    const sent = (row as unknown as Record<string, number>)[field] || 0
    return this.rate(sent, row.enter_uv)
  }

  // 付费率 / 付费N能量率 = 在该书内累计消费果肉达阈值的人数 / 进入剧情人数
  payRate(row: BookNewDataRow, field: string): number {
    const paid = (row as unknown as Record<string, number>)[field] || 0
    return this.rate(paid, row.enter_uv)
  }

  // 导出单个看板。后端按 run_date + board_key 从预计算表取同一份数据生成 xlsx，
  // 前端只负责把 blob 存成文件。
  async handleExportBoard(board: BookDailyBoard) {
    if (!board.rows.length || this.exportingBoard) return
    this.exportingBoard = board.board_key
    try {
      const res = await exportBookDaily(this.runDate, board.board_key)
      // 文件名以服务端返回的为准，解析不出来才用兜底名
      let filename = `分书新增数据-${board.board_name}-${this.runDate}.xlsx`
      const cd = res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])
      const m = cd && /filename\*=UTF-8''([^;]+)/i.exec(cd)
      if (m && m[1]) filename = decodeURIComponent(m[1])

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      // responseType 是 blob，出错时响应体也是 blob，读不出后端的 message，只能给通用提示
      console.error('导出分书新增数据失败:', error)
      this.$message.error('导出失败，请重试')
    } finally {
      this.exportingBoard = ''
    }
  }
}
</script>

<style scoped>
.msg-pending {
  color: #c0c4cc;
}

.board-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.book-new-data-wrapper {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.filter-section {
  margin-bottom: 20px;
}

.filter-content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 15px;
}

.filter-item {
  display: flex;
  align-items: center;
}

.filter-label {
  margin-right: 8px;
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.filter-tip-inline {
  font-size: 13px;
  color: #e6a23c;
}

.filter-tip {
  margin-top: 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.result-section {
  margin-bottom: 20px;
  background: white;
  padding: 15px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.section-title {
  margin: 0 0 6px 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  border-left: 4px solid #409eff;
  padding-left: 10px;
}

.board-sub {
  margin: 0 0 12px 14px;
  font-size: 12px;
  color: #909399;
}

::v-deep .el-table th {
  background-color: #f5f7fa;
}

.book-link {
  color: #409eff;
  text-decoration: none;
  cursor: pointer;
}

.book-link:hover {
  text-decoration: underline;
}
</style>
